import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import { apiRequest, API_BASE_URL } from "../api";
import "../css/multi-user-dashboard.css";
import AddEmailTemplateTemplateModal from "../components/AddEmailTemplateTemplateModal";
import DeployEmailTemplateTemplateModal from "../components/DeployEmailTemplateTemplateModal";

const EmailTemplateTemplateManagement = () => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusMessage, setStatusMessage] = useState(null);
    const [deletingTemplate, setDeletingTemplate] = useState(null);
    const [deletingInstance, setDeletingInstance] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDeployModal, setShowDeployModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [refreshList, setRefreshList] = useState(false);
    const [showInstancesModal, setShowInstancesModal] = useState(false);
    const [instances, setInstances] = useState([]);
    const apiUrl = API_BASE_URL;
    const token = localStorage.getItem("token");

    useEffect(() => {
        fetchTemplates();
    }, [refreshList]);

    const fetchTemplates = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await apiRequest(`${apiUrl}/email-template-templates`, "GET", null, token);
            setTemplates(data);
        } catch (err) {
            setError("Failed to load templates.");
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setSelectedTemplate(null);
        setShowAddModal(true);
    };

    const handleEdit = (template) => {
        setSelectedTemplate(template);
        setShowAddModal(true);
    };

    const handleDelete = async (templateId) => {
        setDeletingTemplate(templateId);
        try {
            await apiRequest(`${apiUrl}/email-template-templates/${templateId}`, "DELETE", null, token);
            setStatusMessage("Template deleted successfully!");
            setRefreshList((prev) => !prev);
        } catch (err) {
            setStatusMessage("Failed to delete template. Please try again.");
        } finally {
            setDeletingTemplate(null);
            setTimeout(() => setStatusMessage(null), 3000);
        }
    };

    const handleDeploy = (template) => {
        setSelectedTemplate(template);
        setShowDeployModal(true);
    };

    const handleViewInstances = async (template) => {
        setSelectedTemplate(template);
        setShowInstancesModal(true);
        try {
            const data = await apiRequest(`${apiUrl}/email-template-templates/${template.id}/instances`, "GET", null, token);
            setInstances(data);
        } catch (err) {
            setInstances([]);
        }
    };

    const handleDeleteInstance = async (templateId, instanceId) => {
        setDeletingInstance(instanceId);
        try {
            await apiRequest(`${apiUrl}/email-template-templates/${templateId}/instances/${instanceId}`, "DELETE", null, token);
            setStatusMessage("Instance deleted successfully!");
            // Refresh the instances list
            const data = await apiRequest(`${apiUrl}/email-template-templates/${templateId}/instances`, "GET", null, token);
            setInstances(data);
        } catch (err) {
            setStatusMessage("Failed to delete instance. Please try again.");
        } finally {
            setDeletingInstance(null);
            setTimeout(() => setStatusMessage(null), 3000);
        }
    };

    if (loading) {
        return (
            <div className="dashboard">
                <Sidebar />
                <main className="main-content">
                    <div className="loading">Loading...</div>
                </main>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard">
                <Sidebar />
                <main className="main-content">
                    <div className="error">{error}</div>
                </main>
            </div>
        );
    }

    return (
        <div className="dashboard">
            <Sidebar />
            <main className="main-content">
                <header className="header component">
                    <span className="component-header">Email Template Templates</span>
                    <button className="btn btn-primary action" onClick={handleAdd}>
                        <i className="fa fa-plus"></i> New Template
                    </button>
                </header>
                <p><a href="/multi-dashboard">Tour Operations Dashboard</a> &gt; Email Template Templates</p>
                <hr />
                {statusMessage && (
                    <div className={`alert ${statusMessage.includes("Failed") ? "alert-danger" : "alert-success"}`} style={{ marginBottom: '20px' }}>
                        {statusMessage}
                    </div>
                )}
                <div className="tours">
                    <table>
                        <thead>
                            <tr>
                                <th>NAME</th>
                                <th>SUBJECT</th>
                                <th>STATUS</th>
                                <th>&nbsp;</th>
                            </tr>
                        </thead>
                        <tbody>
                            {templates.length > 0 ? (
                                templates.map((template) => (
                                    <tr key={template.id}>
                                        <td>{template.name}</td>
                                        <td>{template.subject || "-"}</td>
                                        <td>{template.is_active ? "Active" : "Inactive"}</td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <button className="btn btn-sm btn-view" onClick={() => handleViewInstances(template)}>
                                                    View Instances
                                                </button>
                                                <button className="btn btn-sm btn-primary" onClick={() => handleEdit(template)}>
                                                    Edit
                                                </button>
                                                <button className="btn btn-sm btn-secondary" onClick={() => handleDeploy(template)}>
                                                    Deploy
                                                </button>
                                                <button 
                                                    className="btn btn-sm btn-danger" 
                                                    onClick={() => handleDelete(template.id)}
                                                    disabled={deletingTemplate === template.id}
                                                >
                                                    {deletingTemplate === template.id ? "Deleting..." : "Delete"}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="text-center">No templates found.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {/* Placeholders for modals */}
                {showAddModal && (
                    <AddEmailTemplateTemplateModal
                        emailTemplateTemplate={selectedTemplate}
                        onClose={() => setShowAddModal(false)}
                        onSave={() => {
                            setShowAddModal(false);
                            setRefreshList((prev) => !prev);
                        }}
                    />
                )}
                {showDeployModal && (
                    <DeployEmailTemplateTemplateModal
                        emailTemplateTemplate={selectedTemplate}
                        onClose={() => setShowDeployModal(false)}
                        onDeploy={() => {
                            setShowDeployModal(false);
                            setRefreshList((prev) => !prev);
                        }}
                    />
                )}
                {showInstancesModal && (
                    <div
                        style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            zIndex: 1000,
                        }}
                    >
                        <div
                            style={{
                                backgroundColor: "#fff",
                                padding: "20px",
                                borderRadius: "5px",
                                width: "500px",
                                maxHeight: "90vh",
                                overflowY: "auto",
                            }}
                        >
                            <h4>Linked Email Template Instances</h4>
                            {statusMessage && (
                                <div className={`alert ${statusMessage.includes("Failed") ? "alert-danger" : "alert-success"}`} style={{ marginBottom: '15px' }}>
                                    {statusMessage}
                                </div>
                            )}
                            <ul>
                                {instances.length > 0 ? (
                                    instances.map((inst) => (
                                        <li key={inst.id} style={{ marginBottom: '10px', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <strong>{inst.university_name}</strong> - {inst.email_template_name}
                                                    <br />
                                                    <small style={{ color: '#666' }}>
                                                        Template Controlled: {inst.is_template_controlled ? "Yes" : "No"}
                                                    </small>
                                                </div>
                                                <button
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() => handleDeleteInstance(selectedTemplate.id, inst.id)}
                                                    disabled={deletingInstance === inst.id}
                                                    style={{ marginLeft: '10px' }}
                                                >
                                                    {deletingInstance === inst.id ? "Deleting..." : "Delete"}
                                                </button>
                                            </div>
                                        </li>
                                    ))
                                ) : (
                                    <li>No instances found.</li>
                                )}
                            </ul>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowInstancesModal(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default EmailTemplateTemplateManagement; 